import {CUT_DAY_NAVIGATE_EVENT, CutDayNavigateEventDetail, TimelineWeekCutDayIndicators} from "./timelineWeekCutDayIndicators";
import {TimelineWeekDragScroll} from "./timelineWeekDragScroll";

export type TimelineWeekDayRef = {
	element: HTMLElement;
	date: string;
};

type TimelineWeekHorizontalViewportState = {
	hasHorizontalOverflow: boolean;
	leftCutDayDate: string | null;
	rightCutDayDate: string | null;
};

/**
 * Coordinates all horizontal interactions for the week timeline - including scroll/resize listeners and cleanup:
 * - drag-to-scroll behavior
 * - cut-day indicator rendering
 *
 * Cut-day indicators represent days that are currently clipped by the viewport
 * (`leftCutDayDate` for hidden content on the left, `rightCutDayDate` for hidden content on the right).
 */
export class TimelineWeekHorizontalInteractions {
	private readonly cutDayIndicators: TimelineWeekCutDayIndicators;
	private readonly dragScroll: TimelineWeekDragScroll;

	// Stores the currently scheduled requestAnimationFrame id, if any.
	private pendingViewportUpdateRafId: number | null = null;

	constructor(
		host: HTMLElement,
		private readonly container: HTMLElement,
		private readonly getTimelineDayElements: () => TimelineWeekDayRef[]
	) {
		this.cutDayIndicators = document.createElement("timeline-week-cut-day-indicators") as TimelineWeekCutDayIndicators;
		this.dragScroll = new TimelineWeekDragScroll(host, container);
	}

	/**
	 * Mounts indicators, activates drag-scroll support, and wires viewport listeners.
	 * A first update is requested immediately so UI state is correct before user interaction.
	 */
	public attach(): void {
		// Insert indicator component directly before the timeline container
		this.container.insertAdjacentElement("beforebegin", this.cutDayIndicators);

		// Listen for indicator clicks/keypresses to trigger scroll to hidden day.
		this.cutDayIndicators.addEventListener(CUT_DAY_NAVIGATE_EVENT, this.onCutDayNavigate);

		// Attach drag-scroll handling; enable/disable is decided by viewport state updates (in this component)
		this.dragScroll.attach();

		// Keep indicators in sync when either viewport width or scroll offset changes.
		window.addEventListener("resize", this.requestViewportUpdate);
		this.container.addEventListener("scroll", this.requestViewportUpdate);
		this.requestViewportUpdate();
	}

	/**
	 * Unregisters listeners and UI hooks and cancels outstanding animation-frame work.
	 * This prevents stale updates after the component is removed.
	 */
	public detach(): void {
		if (this.pendingViewportUpdateRafId !== null) {
			globalThis.cancelAnimationFrame(this.pendingViewportUpdateRafId);
			this.pendingViewportUpdateRafId = null;
		}

		window.removeEventListener("resize", this.requestViewportUpdate);
		this.container.removeEventListener("scroll", this.requestViewportUpdate);
		this.cutDayIndicators.removeEventListener(CUT_DAY_NAVIGATE_EVENT, this.onCutDayNavigate);
		this.dragScroll.detach();
		this.cutDayIndicators.remove();
	}

	/**
	 * Requests a viewport-driven UI refresh on the next animation frame.
	 * Multiple rapid triggers (scroll/resize bursts) are coalesced into a single pending callback.
	 */
	public requestViewportUpdate = (): void => {
		// if a new trigger arrived before the previous frame ran — cancel it so only the latest state is processed.
		if (this.pendingViewportUpdateRafId !== null) {
			globalThis.cancelAnimationFrame(this.pendingViewportUpdateRafId);
		}

		this.pendingViewportUpdateRafId = globalThis.requestAnimationFrame(this.update);
	};

	/**
	 * Reads current viewport/day geometry and propagates the resulting state
	 * to drag-scroll behavior and cut-day indicators.
	 */
	private readonly update = (): void => {
		// Current frame callback has started; future triggers may schedule a new one.
		this.pendingViewportUpdateRafId = null;

		const viewportState = this.calculateViewportState(this.getTimelineDayElements());
		this.dragScroll.setEnabled(viewportState.hasHorizontalOverflow);
		this.cutDayIndicators.setEnabled(viewportState.hasHorizontalOverflow);
		this.cutDayIndicators.render({
			leftCutDayDate: viewportState.leftCutDayDate,
			rightCutDayDate: viewportState.rightCutDayDate
		});
	};

	/**
	 * Computes horizontal overflow and clipped-day information from the current DOM state.
	 */
	private calculateViewportState(dayElements: TimelineWeekDayRef[]): TimelineWeekHorizontalViewportState {
		// Container is hidden during loading skeleton — scrollWidth may still exceed clientWidth from previous content.
		const hasHorizontalOverflow = this.container.style.visibility !== "hidden"
			&& this.container.scrollWidth > this.container.clientWidth + 1;

		// Without overflow no day is clipped, so indicators stay hidden/disabled.
		if (!hasHorizontalOverflow) {
			return {
				hasHorizontalOverflow: false,
				leftCutDayDate: null,
				rightCutDayDate: null
			};
		}

		const visibleLeft = this.container.scrollLeft;
		const visibleRight = visibleLeft + this.container.clientWidth;

		// First day whose offsetLeft (integer) is more than 1 px before the visible area is clipped on the left.
		const leftCutDay = dayElements.find(({element}) => element.offsetLeft < visibleLeft);

		// Same fractional-scrollLeft guard as above, applied to the right boundary.
		const rightCutDay = [...dayElements].reverse().find(({element}) => element.offsetLeft + element.offsetWidth > visibleRight);

		return {
			hasHorizontalOverflow: true,
			leftCutDayDate: leftCutDay?.date ?? null,
			rightCutDayDate: rightCutDay?.date ?? null
		};
	}

	private readonly onCutDayNavigate = (event: Event): void => {
		const {direction} = (event as CustomEvent<CutDayNavigateEventDetail>).detail;
		const targetScrollLeft = direction === "left" ? 0 : this.container.scrollWidth - this.container.clientWidth;
		this.container.scrollTo({left: Math.max(0, targetScrollLeft), behavior: "smooth"});
	};
}
